import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { type KeyboardEvent, useId, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Field, FieldLabel } from "@volca/ui/components/ui/field";
import { FilterChip } from "@volca/ui/components/ui/filter-chip";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@volca/ui/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";

export interface MultiSelectOption {
  id: string;
  name: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  label: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  variant?: "field" | "filter";
}

export const MultiSelect = ({
  label,
  options,
  value,
  onChange,
  disabled,
  loading,
  placeholder = "Select",
  variant = "field",
}: MultiSelectProps) => {
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const labelId = useId();
  const triggerId = `${labelId}-trigger`;
  const isFilter = variant === "filter";
  const selected = options.filter((option) => value.includes(option.id));
  const active = selected.length > 0;
  const query = search.trim().toLocaleLowerCase();
  const visible = options.filter((option) =>
    option.name.toLocaleLowerCase().includes(query),
  );
  const toggle = (id: string) =>
    onChange(
      value.includes(id) ? value.filter((item) => item !== id) : [...value, id],
    );
  const handleArrowKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const inputs = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>(
        '[role="checkbox"]:not(:disabled):not([aria-disabled="true"])',
      ) ?? [],
    );
    if (!inputs.length) return;
    const current = inputs.indexOf(document.activeElement as HTMLElement);
    const direction = event.key === "ArrowDown" ? 1 : -1;
    const next =
      current === -1
        ? direction > 0
          ? 0
          : inputs.length - 1
        : (current + direction + inputs.length) % inputs.length;
    inputs[next]?.focus();
  };

  const summary = selected.map((item) => item.name).join(", ");
  const triggerLabel = `${label}: ${summary || placeholder}`;

  const trigger = isFilter ? (
    <PopoverTrigger
      disabled={disabled}
      render={
        <FilterChip
          label={label}
          value={
            active
              ? selected.length === 1
                ? selected[0].name
                : `${selected.length} selected`
              : undefined
          }
          placeholder={placeholder}
          onClear={() => onChange([])}
          disabled={disabled}
          aria-label={triggerLabel}
          title={active ? summary : undefined}
        />
      }
    />
  ) : (
    <Stack direction="row" align="center" gap={1} className="min-w-0">
      <PopoverTrigger
        id={triggerId}
        disabled={disabled}
        render={
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={triggerLabel}
            className="min-w-0 flex-1 justify-between font-normal"
          >
            <span
              className={cn("truncate", {
                "text-subtle": !active,
              })}
              title={active ? summary : undefined}
            >
              {selected[0]?.name ?? placeholder}
            </span>
            {selected.length > 1 && (
              <span className="ml-auto shrink-0 text-subtle">
                +{selected.length - 1}
              </span>
            )}
            <CaretDownIcon
              aria-hidden
              data-icon="inline-end"
              className="text-subtle"
            />
          </Button>
        }
      />
      {/* Clears every value, as the filter chip's does: the field shows
          only the first, so removing just that one looked like nothing. */}
      {active && (
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          disabled={disabled}
          aria-label={`Clear ${label}`}
          onClick={() => onChange([])}
        >
          <XIcon aria-hidden />
        </Button>
      )}
    </Stack>
  );

  const popover = (
    <Popover
      onOpenChange={(open: boolean) => {
        if (!open) setSearch("");
      }}
    >
      {trigger}
      {/* Opens on the search, not on "Clear all", the first thing in it. */}
      <PopoverContent
        align="start"
        initialFocus={searchRef}
        aria-label={label}
        className="w-72 gap-0 overflow-hidden p-0"
      >
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap={2}
          className="w-full border-b border-line px-3 py-2"
        >
          <Text variant="label">{label}</Text>
          {value.length > 0 && (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() => onChange([])}
            >
              Clear all
            </Button>
          )}
        </Stack>
        <Stack
          align="start"
          gap={0}
          className="w-full border-b border-line px-3 py-2 [&>div]:w-full"
        >
          <InputGroup>
            <InputGroupAddon align="inline-start">
              <MagnifyingGlassIcon />
            </InputGroupAddon>
            <InputGroupInput
              ref={searchRef}
              aria-label={`Search ${label}`}
              placeholder="Search values"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleArrowKey}
            />
          </InputGroup>
        </Stack>
        <Stack
          align="start"
          ref={listRef}
          role="group"
          aria-label={label}
          gap={0}
          className="max-h-64 w-full overflow-y-auto p-1"
          onKeyDown={handleArrowKey}
        >
          {loading ? (
            <span role="status" className="px-3 py-4 text-sm">
              Loading…
            </span>
          ) : visible.length === 0 ? (
            <span role="status" className="px-3 py-4 text-sm text-subtle">
              No options found
            </span>
          ) : (
            visible.map((option) => (
              <label
                key={option.id}
                htmlFor={`${labelId}-${option.id}`}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
                  {
                    "cursor-pointer hover:bg-neutral-hovered": !option.disabled,
                    "opacity-50": option.disabled,
                  },
                )}
              >
                <Checkbox
                  id={`${labelId}-${option.id}`}
                  checked={value.includes(option.id)}
                  disabled={option.disabled}
                  onCheckedChange={() => toggle(option.id)}
                />
                <span
                  className="min-w-0 flex-1 truncate text-sm text-ink"
                  title={option.name}
                >
                  {option.name}
                </span>
              </label>
            ))
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );

  if (isFilter) return popover;

  return (
    <Field data-disabled={disabled || undefined} className="min-w-0">
      <FieldLabel htmlFor={triggerId}>{label}</FieldLabel>
      {popover}
    </Field>
  );
};
